import { EligibleBenchmarkOptions } from 'state';

export type LibKind = 'index_fund' | 'equity';

/** A flattened, display-ready eligible benchmark option (fund or equity). */
export interface LibOption {
  ric: string;
  name: string;
  issuer: string;
  kind: LibKind;
  /** Admin free-text classification, funds only (undefined for equities / when blank). */
  assetClass?: string;
  geography?: string;
  /** Retired fund kept in the payload only because this session already holds it: filtered out of
   *  the add-list, but still rendered (badged) on the basket it belongs to. Funds only. */
  decommissioned?: boolean;
  /** Coverage-gate (admin recompute drawer only). Set when a per-option coverage overlay is passed
   *  to toLibOptions and the option is NOT fully covered over the recompute dates: the row renders
   *  disabled (not addable) with `disabledReason`. Undefined everywhere else (trainer library). */
  disabled?: boolean;
  disabledReason?: string;
  missingCount?: number;
}

/** Per-option coverage (from the benchmark coverage diagnostics), keyed by RIC. */
export interface OptionCoverage {
  currently_eligible: boolean;
  missing_count: number;
}

/** One selected constituent of the shared benchmark basket. `weight` is stored
 *  in PERCENT (0–100) for editing; it is converted to a 0–1 fraction only at
 *  dispatch time, matching the classic editor's wire contract. */
export interface BasketRow {
  ric: string;
  weight: number;
}

/** Overlay a per-option coverage gate: an option that isn't fully covered becomes disabled +
 *  reasoned. No-op when `coverage` is absent (trainer library / historical drawer). */
const withCoverage = (
  option: LibOption,
  coverage?: OptionCoverage,
): LibOption => {
  if (!coverage || coverage.currently_eligible) return option;
  const n = coverage.missing_count;
  return {
    ...option,
    disabled: true,
    missingCount: n,
    disabledReason: `price missing on ${n} date${n === 1 ? '' : 's'}; check diagnostics to fix`,
  };
};

/** Flatten eligible index funds + equities into one searchable list. When `coverageByRic` is
 *  supplied (admin recompute drawer, real-time), each option is coverage-gated (see withCoverage);
 *  omitted everywhere else so behaviour is unchanged. */
export const toLibOptions = (
  eligible: EligibleBenchmarkOptions | undefined,
  coverageByRic?: Record<string, OptionCoverage>,
): LibOption[] => {
  if (!eligible) return [];
  const funds: LibOption[] = (eligible.index_funds || []).map(f =>
    withCoverage(
      {
        ric: f.ric,
        name: f.name,
        issuer: f.issuer_name || '',
        kind: 'index_fund' as const,
        assetClass: f.asset_class || undefined,
        geography: f.geography || undefined,
        decommissioned: f.decommissioned,
      },
      coverageByRic?.[f.ric],
    ),
  );
  const equities: LibOption[] = (eligible.equities || []).map(e =>
    withCoverage(
      {
        ric: e.ric,
        name: e.name,
        issuer: '',
        kind: 'equity' as const,
      },
      coverageByRic?.[e.ric],
    ),
  );
  return [...funds, ...equities];
};

/** ric -> LibOption, for resolving a basket row's display name / type. */
export const indexByRic = (options: LibOption[]): Record<string, LibOption> => {
  const map: Record<string, LibOption> = {};
  options.forEach(o => {
    map[o.ric] = o;
  });
  return map;
};

export const weightTotal = (basket: BasketRow[]): number =>
  basket.reduce((sum, r) => sum + (Number.isFinite(r.weight) ? r.weight : 0), 0);

export interface BasketValidation {
  valid: boolean;
  /** short human reason when invalid, '' when valid */
  reason: string;
}

/** Mirrors the backend rule: non-empty, each weight in [1, 100], sum == 100
 *  (±0.01). Surfaced live in the UI instead of as a post-save notification. */
export const validateBasket = (basket: BasketRow[]): BasketValidation => {
  if (basket.length === 0) {
    return { valid: false, reason: 'Add at least one constituent.' };
  }
  if (basket.some(r => r.weight == null || r.weight < 1 || r.weight > 100)) {
    return { valid: false, reason: 'Each weight must be between 1% and 100%.' };
  }
  const total = weightTotal(basket);
  if (Math.abs(total - 100) > 0.01) {
    const delta = total - 100;
    return {
      valid: false,
      reason: `Weights total ${total.toFixed(1)}% — ${
        delta > 0 ? 'over' : 'under'
      } by ${Math.abs(delta).toFixed(1)}%.`,
    };
  }
  return { valid: true, reason: '' };
};

/** Round to 1 decimal, avoiding -0 and float fuzz. */
export const round1 = (n: number): number => Math.round(n * 10) / 10;

/** Spread 100% as evenly as possible, putting the rounding remainder on row 0
 *  so the basket always sums to exactly 100. */
export const evenSplit = (basket: BasketRow[]): BasketRow[] => {
  const n = basket.length;
  if (n === 0) return basket;
  const base = round1(100 / n);
  return basket.map((row, i) => ({
    ...row,
    weight: i === 0 ? round1(100 - base * (n - 1)) : base,
  }));
};
